import { useState } from 'react';
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { Link, useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { useResilientAction } from '@/hooks/useResilientAction';
import { signIn } from '@/utils/auth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Phone, Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export function Login() {
  const { t, currentLang } = useI18n();
  const navigate = useNavigate();

  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const { isLoading, execute: resilientSignIn } = useResilientAction(
    async (signal: AbortSignal, phoneStr: string, passwordStr: string) => {
      void signal;
      const { error: signInError } = await signIn(phoneStr, passwordStr);
      if (signInError) throw signInError;
      return true;
    }
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const result = await resilientSignIn(phone, password);
      if (result) {
        navigate('/');
      }
    } catch (err: unknown) {
      if (import.meta.env.DEV) console.error('[Login] Error:', err);
      const errObj = err as Record<string, unknown>;
      const errMessage = typeof errObj?.message === 'string' ? errObj.message : String(err);
      let message = errMessage || (currentLang === 'bn' ? 'লগইন ব্যর্থ হয়েছে' : 'Login failed');
      
      if (errMessage?.includes('Email not confirmed')) {
        message = currentLang === 'bn' 
          ? 'আপনার ইমেইল ভেরিফাই করা হয়নি। দয়া করে আবার চেষ্টা করুন।' 
          : 'Email not verified. Please try again.';
      } else if (errMessage?.includes('Invalid login credentials')) {
        message = currentLang === 'bn'
          ? 'ভুল ইমেইল বা পাসওয়ার্ড'
          : 'Invalid email or password';
      }

      toast.error(message);
    }
  };



  return (
    <div className="flex items-center justify-center bg-white py-20 px-4">
      <div className="w-full max-w-md mx-auto">
        <Card className="rounded-none shadow-[0px_3px_6px_rgba(0,0,0,0.2)]">
          <CardHeader className="text-center">
            <div className="mx-auto -m-8 w-[200px] h-[200px] rounded-none overflow-hidden flex items-center justify-center">
              <img src="/images/logo-png.png" alt="App Icon"/>
            </div>  
            <CardTitle className="text-2xl">{t('login.title')}</CardTitle>
            <CardDescription className='text-black'>{t('login.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-3">


              <div className="space-y-2">
                <Label htmlFor="phone">{t('form.phone')}</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                  <Input
                    id="phone"
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    className="pl-10"
                    placeholder="01XXXXXXXXX"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t('form.password')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-600" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 pr-10"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <FaEyeSlash className="h-4 w-4" />
                    ) : (
                      <FaEye className="h-4 w-4" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="remember"
                    checked={rememberMe}
                    onCheckedChange={(checked: boolean | 'indeterminate') =>
                      setRememberMe(checked === true)
                    }
                  />
                  <Label htmlFor="remember" className="text-sm">
                    {currentLang === 'bn' ? 'মনে রাখুন' : 'Remember me'}
                  </Label>
                </div>
                {/* Forgot password removed for now */}
              </div>

              <Button type="submit" className="w-full shadow-[0_2px_2px_rgba(0,0,0,0.4)]" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {currentLang === 'bn' ? 'লগইন হচ্ছে...' : 'Logging in...'}
                  </>
                ) : (
                  t('nav.login')
                )}
              </Button>

            </form>

            <p className="text-center text-sm mt-6">
              {t('login.no_account')}{' '}
              <Link to="/register" className="text-primary hover:underline font-medium">
                {t('login.register')}
              </Link>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
